import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginUser, registerUser, logoutUser, getUserData, updateUserData, requestPasswordReset, verifyResetCode, changePassword } from '../../api/services/authService';

// Async thunks
export const loginUserAction = createAsyncThunk(
  'auth/login',
  async ({ email, password, rememberMe = false }, { rejectWithValue }) => {
    try {
      const response = await loginUser(email, password, rememberMe);
      return response;
    } catch (error) {
      // error is already formatted by interceptor: { status, message }
      return rejectWithValue(error?.message || 'Login failed');
    }
  }
);

export const registerUserAction = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await registerUser(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Registration failed');
    }
  }
);

export const logoutUserAction = createAsyncThunk(
  'auth/logout',
  async () => {
    await logoutUser();
    return null;
  }
);

export const fetchUserDataAction = createAsyncThunk(
  'auth/fetchUserData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserData();
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch user');
    }
  }
);

export const updateUserDataAction = createAsyncThunk(
  'auth/updateUserData',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateUserData(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to update profile');
    }
  }
);

// Forgot password - request code
export const forgotPasswordAction = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await requestPasswordReset(email);
      return response;
    } catch (error) {
      // Extract error message from API response
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error || 
        error?.message || 
        'Failed to request password reset';
      return rejectWithValue(errorMessage);
    }
  }
);

// Verify code
export const verifyCodeAction = createAsyncThunk(
  'auth/verifyCode',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const response = await verifyResetCode(email, code);
      return response;
    } catch (error) {
      // Extract error message from API response
      // Handle both error formats: { message: "..." } and { errors: [{ msg: "..." }] }
      const errorData = error?.response?.data;
      let errorMessage = 'Invalid or expired verification code';
      
      if (errorData) {
        // Check for errors array format
        if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors[0].msg || errorMessage;
          // Return the full error data structure to preserve error details
          return rejectWithValue({ errors: errorData.errors, message: errorMessage });
        }
        // Check for message format
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Change password
export const changePasswordAction = createAsyncThunk(
  'auth/changePassword',
  async ({ email, code, newPassword, confirmPassword }, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.auth?.resetToken || null;
      const response = await changePassword(email, code, newPassword, confirmPassword, token);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to change password');
    }
  }
);

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('authToken') || null,
  // A stored token means "logged in" from the very first render — otherwise a hard
  // refresh on any RequireAuth page bounces to /login before auth rehydrates
  // (expired tokens are caught by the axios 401 interceptor and logged out).
  isAuthenticated: !!localStorage.getItem('authToken'),
  loading: false,
  error: null,
  resetEmail: null,
  resetToken: null,
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setResetEmail: (state, action) => {
      state.resetEmail = action.payload || null;
    },
    clearResetFlow: (state) => {
      state.resetEmail = null;
      state.resetToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUserAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUserAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user || null;
        state.token = action.payload.token;
        state.isAuthenticated = !!action.payload.token;
        if (action.payload.token) {
          localStorage.setItem('authToken', action.payload.token);
        }
        // Note: Profile data will be fetched automatically via a separate dispatch in the component
      })
      .addCase(loginUserAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
      })
      // Register
      .addCase(registerUserAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUserAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload?.user || null;
        const token = action.payload?.accessToken || action.payload?.token || null;
        if (token) {
          state.token = token;
          state.isAuthenticated = true;
          localStorage.setItem('authToken', token);
        } else {
          // No token returned on register; keep user unauthenticated
          state.token = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(registerUserAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUserAction.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem('authToken');
      })
      // Fetch User Data
      .addCase(fetchUserDataAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserDataAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchUserDataAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // isAuthenticated starts from the stored token. If that token turned out to be expired
        // or revoked, the 401/403 handler has already removed it — reflect that here so
        // protected pages send the user to login instead of rendering logged-in with no data.
        if (!localStorage.getItem('authToken')) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
        }
      })
      // Update User Data
      .addCase(updateUserDataAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserDataAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload; // updated user object
      })
      .addCase(updateUserDataAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Forgot Password
      .addCase(forgotPasswordAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPasswordAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify Code
      .addCase(verifyCodeAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyCodeAction.fulfilled, (state, action) => {
        state.loading = false;
        state.resetToken = action.payload?.token || action.payload?.accessToken || null;
      })
      .addCase(verifyCodeAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Change Password
      .addCase(changePasswordAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePasswordAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePasswordAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setToken, setResetEmail } = authSlice.actions;
export default authSlice.reducer;
